﻿import { ObjectUtils } from '../main/ObjectUtils.js';
import { ProductViewModelFactories } from './ProductViewModelFactories.js';

// TODO: Rename Component to Product
/**
 * The Component class is composition of a Langlo Designer API product and a display object.
 *
 * Properties:
 *   - displayObject - a rendering-engine specific object, e.g. a element in an HTML DOM or a
 *     Pixi Sprite, Graphics, or Container object (WebGL based 2D display engine). Each display
 *     object will have a dataComponent property back-referencing this component object.
 *   - apiProduct - a data-transfer object (DTO) from the Designer server containing the properties
 *     needed for displaying and editing.
 *   - drawingService - provides rendering-engine specific methods.
 *   - rect - the size and position of the component in cm units using Designer's bottom-left based
 *     coord system. The units are resolution independent since the entire drawing will be scaled to
 *     fit within the parent html element.
 *   - finish - a Langlo Designer compatible product finish Id.
 *   - components - each component may have zero or more child components.
 */
export class Component {
  constructor(name, drawingService, apiProduct, config) {
    this.name = name;
    this.type = apiProduct.type;
    this.drawingService = drawingService;
    this._displayObject = null;
    this.components = [];
    /* Note, Component classes use a bottom/left/back oriented coordinate system. rect objects have
     * left, bottom, width, and height properties. There's a getTopLeftRect() method for converting
     * to a top/left oriented rectangle. */
    const p = this.apiProduct = apiProduct;
    p.l = p.l || 0; // Left
    p.b = p.b || 0; // Bottom
    p.z = p.z || 0; // Back
    p.w = p.w || 0; // Width
    p.h = p.h || 0; // Height
    p.d = p.d || 0; // Depth
    // Bottom-left oriented coordinates:
    this.rect = {
      left: p.l,
      bottom: p.b,
      back: p.z,
      width: p.w,
      height: p.h,
      depth: p.d,
    };

    // Assign rotation in the "x,y,z" format from the apiProduct
    if (p.r) {
      const xyz = p.r.split(',');
      this.rotation = {
        x: parseFloat(xyz[0]),
        y: parseFloat(xyz[1]),
        z: parseFloat(xyz[2]),
      }
    } else {
      this.rotation = { x: 0, y: 0, z: 0 };
    }

    this.preFactorySetup(config);
    this.factorySetup(config);
  }

  preFactorySetup(config) {
    // TODO: Rename to finishId
    this.finish = this.apiProduct.f;
    this.materialIdPrefix = null;
  }

  factorySetup(config) {
    this.updateMaterial();
  }

  updateMaterial(finishId) {
    finishId = finishId || this.finish;
    const material = this.materialIdPrefix
      ? this.drawingService.getMaterial(this.materialIdPrefix, finishId)
      : null;
    return this.material = material;
  }

  finishChanged() {
    // Note, the drawingService will know whether this component class actually needs a label
    // DONE: Enable once we have Visitor classes for updating finish labels
    this.drawingService.updateFinishLabel(this);
  }

  /**
   * Lightweight method updating this.finish and optionally this.material. No other side effects.
   * @param {any} value The new finish id
   * @param {any} preventNotification If the new finish is different and preventNotification is not true,
   * then finishChanged() method will be called.
   * @returns true if the finish was changed.
   */
  setFinish(value, preventNotification) {
    if (value !== this.finish) {
      this.finish = value;
      this.updateMaterial();
      if (!preventNotification) {
        this.finishChanged();
      }
      return true;
    }
    return false;
  }

  /**
   * Updates this.finish and indirectly this.material. this.drawingService will be accessed for
   * updating the UI.
   * @param {any} newFinish The new finish Id
   * @param {any} onChanged A callback called if the finish was changed.
   * @returns true if the finish was changed.
   */
  replaceFinish(newFinish, onChanged) {
    if (newFinish !== this.finish) {
      const oldFinish = this.finish;
      const oldDisplayObject = this.displayObject;
      if (this.drawingService.replaceFinishInline(this, newFinish)) {
        this.setFinish(newFinish);
      } else {
        /* It was not possible to replace the finish on the existing display object. Instead,
         * we must create a new displayObject with the new finish. */
        this.setFinish(newFinish, /*preventNotification:*/ true); // <-- false because we're making the finishChanged call below
        const newDisplayObject = this.createMaterialObject();
        this.displayObject = newDisplayObject;
        this.drawingService.replaceDisplayObject(oldDisplayObject, newDisplayObject);
        this.finishChanged();
      }
      if (onChanged) {
        onChanged(this, { oldFinish, oldDisplayObject });
      }
      return true;
    }
    return false;
  }

  /** Virtual method which can be overridden to be more specific in which components to recurse into. */
  doReplaceFinishesRecursively(newFinish, onChanged) {
    this.components.forEach(comp => comp.doReplaceFinishesRecursively(newFinish));
    this.replaceFinish(newFinish, onChanged);
  }

  replaceFinishesRecursively(newFinish, onChanged) {
    this.drawingService.disableDrawingUpdates();
    try {
      this.doReplaceFinishesRecursively(newFinish, onChanged);
    } finally {
      if (this.drawingService.enableDrawingUpdates()) {
        this.drawingService.updateAllFinishesInline(this);
      }
    }
  }

  getSecondaryFinish() {
    // We intentionally don't return anything in the base method. Undefined: SecondaryFinish is not used for this component
  }

  setSecondaryFinish(value) {
  }

  /**
   * Updates the secondary finish.
   * @param {any} newFinish The new finish Id
   * @param {any} onChanged A callback called if the finish was changed.
   * @returns true if the finish was changed.
   */
  replaceSecondaryFinish(newFinish, onChanged) {
    const oldFinish = this.getSecondaryFinish();
    if (typeof oldFinish !== 'undefined' && newFinish !== oldFinish) {
      const oldDisplayObject = this.displayObject;
      this.setSecondaryFinish(newFinish);
      if (onChanged) {
        onChanged(this, { oldFinish, oldDisplayObject });
      }
      return true;
    }
    return false;
  }

  /** Virtual method which can be overridden to be more specific in which components to recurse into. */
  doReplaceSecondaryFinishesRecursively(newFinish, onChanged) {
    this.components.forEach(comp => comp.doReplaceSecondaryFinishesRecursively(newFinish));
    this.replaceSecondaryFinish(newFinish, onChanged);
  }

  replaceSecondaryFinishesRecursively(newFinish, onChanged) {
    this.drawingService.disableDrawingUpdates();
    try {
      this.doReplaceSecondaryFinishesRecursively(newFinish, onChanged);
    } finally {
      if (this.drawingService.enableDrawingUpdates()) {
        this.drawingService.performInlineUpdate(this);
      }
    }
  }

  // [Obsolete('Not used anymore')]
  replaceChildFinish(child, newFinish, onChanged) {
    child.replaceFinish(newFinish, (sender, { oldFinish, oldChildDisplayObject }) => {
      if (onChanged) {
        onChanged(this, { child, oldFinish, oldChildDisplayObject });
      }
    });
  }

  get displayObject() {
    return this._displayObject;
  }

  set displayObject(value) {
    if (value !== this._displayObject) {
      if (this._displayObject) {
        this._displayObject.dataComponent = null;
      }
      this._displayObject = value;
      if (this._displayObject) {
        this._displayObject.dataComponent = this;
      }
    }
  }

  /**
   * Creates a display object where the product's material is shown.
   */
  createMaterialObject() {
    return this.drawingService.createMaterialObject(this);
  }

  /**
   * Creates a light-weight display object whose only purpose is to contain child display objects.
   */
  createContainer() {
    return this.drawingService.createContainer(this);
  }

  /**
   * Semi-abstract method to be overridden by subclasses which have to decide whether to create
   * a material-based display object, or a light-weight container object.
   */
  createDisplayObject() {
  }

  /**
   * Factory method for creating a component instance.
   */
  createProduct(name, apiProduct, config) {
    const product = ProductViewModelFactories.createProduct(this.drawingService, apiProduct, config);
    product.name = name;
    return product;
  }

  /**
   * Recursively removes and destroys all display objects from this component and its descendants.
   * Note, in addition to being used internally in this class, this method is also called when the
   * drawing needs to replace one product with another (it first calls removeDisplayObject on the
   * old product).
   */
  removeDisplayObject() {
    this.components.forEach(child => child.removeDisplayObject());
    if (this.displayObject) {
      this.drawingService.removeChildDisplayObject(this.displayObject);
      this.displayObject = null;
    }
  }

  /**
   * Recursively creates and adds display objects for this component and its children.
   * this.displayObject property will be assigned in the process. Also adds the displayObject as a
   * child of the parent's displayObject. If this is the root component, then this call effectively
   * adds all the displayObjects to the drawing.
   */
  addDisplayObject() {
    if (!this.displayObject) {
      this.displayObject = this.createDisplayObject();
      this.components.forEach(child => child.addDisplayObject());
      this.drawingService.addChildDisplayObject(this);
    }
    return this.displayObject;
  }

  /**
   * Perform necessary clean up to help the browser's memory management and to avoid leaks.
   * This method is called by the DetachFromDomIterator. It's the iterator's responsibility to iterate
   * children, hence don't iterate into child components directly from this method.
   */
  detachFromUI() {
    this.displayObject = null;
    this.drawingService.detachFinishLabel(this);
  }

  addComponent(component) {
    this.components.push(component);
    component.parent = this;
    return component;
  }

  removeComponent(component) {
    const index = this.components.indexOf(component);
    if (index > -1) {
      component.parent = null;
      this.components.splice(index, 1);
    }
    return index;
  }

  /**
   * Helper method for adding components from the apiComponents array to ourselves. The new component
   * array will be the return value of the method.
   * @param {any} name Will be used as the prefix in the component's name
   * @param {any} apiComponents The array of components from an API product.
   */
  addApiComponents(name, apiComponents) {
    const components = [];
    if (apiComponents) {
      let i = 1;
      apiComponents.forEach(apiComponent => {
        const component = this.createProduct(`${name} ${i} (${apiComponent.type})`, apiComponent);
        this.addComponent(component);
        components.push(component);
        i++;
      });
    }
    return components;
  }

  toggleFinishLabels(on) {
    // Note, the drawingService will know whether this component class actually needs a label
    this.drawingService.toggleFinishLabels(this, on);
  }

  updateHeight(value) {
    if (value !== this.rect.height) {
      this.rect.height = value;
      return true;
    }
    return false;
  }

  updateWidth(value) {
    if (value !== this.rect.width) {
      this.rect.width = value;
      return true;
    }
    return false;
  }

  updateDepth(value) {
    if (value !== this.rect.depth) {
      this.rect.depth = value;
      return true;
    }
    return false;
  }

  /** Compatible with the Iterator class. The purpose of this method is simply to recurse the
   * component graph. Subclasses should call iterator.iterate(child) for each component.
   */
  iterate(iterator) {
  }

  visitChildren(visitor) {
  }

  acceptVisitor(visitor) {
    return visitor.visitComponent(this);
  }

  static shrinkRect(rect, delta) {
    return {
      left: rect.left + delta.left,
      bottom: rect.bottom + delta.bottom,
      back: rect.back + delta.back,
      width: rect.width - delta.left - delta.right,
      height: rect.height - delta.top - delta.bottom,
      depth: rect.depth - delta.back - delta.front,
    };
  }

  static growRect(rect, delta) {
    return {
      left: rect.left - delta.left,
      bottom: rect.bottom - delta.bottom,
      back: rect.back - delta.back,
      width: rect.width + delta.left + delta.right,
      height: rect.height + delta.top + delta.bottom,
      depth: rect.depth + delta.back - delta.front,
    };
  }

  /** Returns the top-left based vertical position of a child component. */
  to2dTop(childBottom, childHeight) {
    return this.rect.height - childBottom - childHeight;
  }

  /**
   * Returns the component's rect in a top-left-based coordinate system. top/left are relative to
   * the parent component.
   */
  getTopLeftRect() {
    const top = this.parent ? this.parent.to2dTop(this.rect.bottom, this.rect.height) : 0;
    return {
      left: this.rect.left,
      top: top,
      back: this.rect.back,
      width: this.rect.width,
      height: this.rect.height,
      depth: this.rect.depth,
    };
  }

  /** Returns this component's relative top position when converted to a top-left based coordinate system. */
  getRelative2dTop() {
    if (this.parent) {
      const top = this.parent.to2dTop(this.rect.bottom, this.rect.height);
      return top / this.parent.rect.height;
    } else {
      return 0;
    }
  }

  /** Returns this component's relative left position. */
  getRelative2dLeft(leftCorrection, parentWidthCorrection) {
    if (this.parent) {
      let left = this.rect.left;
      if (leftCorrection)
        left += leftCorrection;
      let parentWidth = this.parent.rect.width;
      if (parentWidthCorrection) {
        parentWidth += parentWidthCorrection;
      }
      return left / parentWidth;
    } else {
      return 0;
    }
  }

  addToMaterialIdList(list) {
    this.components.forEach(child => child.addToMaterialIdList(list));
    if (this.material) {
      // Let's use the actual material already found (might be a fallback material/general finish):
      list[this.material.id] = true;
    } else if (this.finish) {
      if (this.materialIdPrefix) {
        list[this.materialIdPrefix + this.drawingService.materialPrefixDelim + this.finish] = true;
      } else {
        list[this.finish] = true;
      }
    }
  }

  createMaterialIdList() {
    const list = {};
    this.addToMaterialIdList(list);
    return list;
  }

  /**
   * Adds the product's primary finishes to the finishes array parameter. Note, secondary finishes
   * should not be added (frame finish on doors, metal finish in flexi sections, etc.).
   */
  addToPrimaryFinishArray(finishes) {
    if (!finishes.includes(this.finish)) {
      finishes.push(this.finish);
    }
  }

  /**
   * Adds the product's primary finishes to the finishes array parameter. Note, secondary finishes
   * should not be added (frame finish on doors, metal finish in flexi sections, etc.).
   */
  addToSecondaryFinishArray(finishes) {
    const finish = this.getSecondaryFinish();
    if (finish && !finishes.includes(finish)) {
      finishes.push(finish);
    }
  }

  /**
   * Returns an array of the product's primary finishes (id's). Note, secondary finishes should
   * not be added (frame finish on doors, metal finish in flexi sections, etc.).
   */
  getAllPrimaryFinishes() {
    let finishes = [];
    this.addToPrimaryFinishArray(finishes);
    return finishes;
  }

  /**
   * Returns an array of the product's secondary finishes (frame finish on doors, metal finish in
   * flexi sections, etc.).
   */
  getAllSecondaryFinishes() {
    let finishes = [];
    this.addToSecondaryFinishArray(finishes);
    return finishes;
  }

  static assignArrayModificationsTo(destApiProduct, arrayName, sourceArray, assignType) {
    if (!sourceArray) {
      return false;
    }
    let isModified = false;
    let index = 0;
    let destArray = null;
    let lastModNdx = -1;
    sourceArray.forEach(product => {
      const destProduct = {};
      if (assignType) {
        destProduct.type = product.type;
      }
      if (product.assignModificationsTo(destProduct)) {
        destArray = destArray || Array(sourceArray.length).fill({});
        destArray[index] = destProduct;
        isModified = true;
        lastModNdx = index;
      }
      index++;
    });
    if (isModified) {
      destApiProduct[arrayName] = destArray.slice(0, lastModNdx + 1);
    }
    return isModified;
  }

  /**
   * Returns the ratio of this component's width relative to the component's height (width / height).
   * @param {any} use2D If true, then use the rotation property to determine which lengths to be used
   * in the calculation.
   * @param {any} rotation If defined, overrides this.rotation.
   */
  getWidthHeightRatio(use2D = null, rotation = null) {
    if (use2D) {
      rotation = rotation || this.rotation;
      if (rotation.x === 90) {
        return this.rect.depth ? this.rect.width / this.rect.depth : NaN;
      }
      if (rotation.y === 90) {
        return this.rect.height ? this.rect.depth / this.rect.height : NaN;
      }
    }
    return this.rect.height ? this.rect.width / this.rect.height : NaN;
  }

  /** Returns the ratio of this component's width relative to the component's depth (width / depth). */
  getWidthDepthRatio() {
    return this.rect.depth ? this.rect.width / this.rect.depth : NaN;
  }

  /** Returns the ratio of the material's width relative to the material's height (width / height). */
  getMaterialWidthHeightRatio() {
    return this.material ? this.material.productWidth / this.material.productHeight : NaN;
  }

  /**
   * Returns the ratio of the width relative to the parent component's width.
   * @param {double} width The width in mm which we want to convert to a percent of the parent's width.
   * If width is not provided, then the component's width is used instead.
   * @param {any} use2D If true, then use the rotation property to determine which lengths to be used
   * in the calculation.
   * @param {any} rotation If defined, overrides this.rotation.
   */
  getWidthRatio(width, use2D = null, rotation = null, parentWidthCorrection = null) {
    if (width === 0) {
      return 0; // Avoid confusion of undefined vs 0
    }

    let parentWidth = this.parent ? this.parent.rect.width : 0;
    if (parentWidth && parentWidthCorrection) {
      parentWidth += parentWidthCorrection;
    }

    if (!width) {
      rotation = rotation || this.rotation;
      width = use2D && rotation.y === 90 ? this.rect.depth : this.rect.width;
    }

    return parentWidth ? width / parentWidth : NaN;
  }

  /**
   * Returns the ratio of this component's height relative to the parent component's height.
   * @param {any} use2D If true, then use the rotation property to determine which lengths to be used
   * in the calculation.
   * @param {any} rotation If defined, overrides this.rotation.
   */
  getHeightRatio(decimals, use2D = null, rotation = null) {
    const parentHeight = this.parent ? this.parent.rect.height : 0;
    if (parentHeight) {
      rotation = rotation || this.rotation;
      const height = use2D && rotation.x === 90 ? this.rect.depth : this.rect.height;
      const exactRatio = height / parentHeight;
      return decimals || decimals === 0 ? ObjectUtils.roundDec(exactRatio, decimals) : exactRatio;
    }
    return NaN;
  }

  /** Returns the ratio of this component's depth relative to the parent component's depth. */
  getDepthRatio(decimals) {
    const parentDepth = this.parent ? this.parent.rect.depth : 0;
    if (parentDepth) {
      const exactRatio = this.rect.depth / parentDepth;
      return decimals ? ObjectUtils.roundDec(exactRatio, decimals) : exactRatio;
    }
    return NaN;
  }

  /**
   * Returns the ratio of this component's width relative to the material's width. A ratio larger
   * than 1.0 means the component is wider than the material's width (shouldn't happen unless the
   * material image has been taken of a component narrower than the max. available).
   * @param {any} use2D If true, then use the rotation property to determine which length to use as
   * the width in the calculation.
   * @param {any} rotation If defined, overrides this.rotation.
   */
  getMaterialWidthRatio(use2D = null, rotation = null) {
    let width = this.rect.width;
    if (use2D) {
      rotation = rotation || this.rotation;
      if (rotation.y === 90) {
        width = this.rect.depth;
      }
    }
    const materialWidth = this.material ? this.material.productWidth : 0;
    return materialWidth ? width / materialWidth : NaN;
  }

  /**
   * Returns the ratio of this component's height relative to the material's height. A ratio larger
   * than 1.0 means the component is taller than the material's height (shouldn't happen unless the
   * material image has been taken of a component shorter than the max. available).
   * @param {any} use2D If true, then use the rotation property to determine which length to use as
   * the height in the calculation.
   * @param {any} rotation If defined, overrides this.rotation.
   */
  getMaterialHeightRatio(use2D = null, rotation = null) {
    let height = this.rect.height;
    if (use2D) {
      rotation = rotation || this.rotation;
      if (rotation.x === 90) {
        height = this.rect.depth;
      }
    }
    const materialHeight = this.material ? this.material.productHeight : 0;
    return materialHeight ? height / materialHeight : NaN;
  }

  /** Returns the ratio of this component's depth relative to the material's height. A ratio larger
   * than 1.0 means the component is deeper than the material's height (shouldn't happen unless the
   * material image has been taken of a component shorter than the max. available).
   * @param {any} use2D If true, then use the rotation property to determine which length to use as
   * the depth in the calculation.
   * @param {any} rotation If defined, overrides this.rotation.
   */
  getMaterialDepthRatio(use2D = null, rotation = null) {
    let depth = this.rect.depth;
    if (use2D) {
      rotation = rotation || this.rotation;
      if (rotation.x === 90) {
        depth = this.rect.height;
      }
    }
    const materialHeight = this.material ? this.material.productHeight : 0;
    return materialHeight ? depth / materialHeight : NaN;
  }

  /**
   * Returns clientWidth x getWidthRatio(), where getWidthRatio() is the ratio of this component's
   * width relative to the parent component's width.
   * @param {any} use2D If true, then use the rotation property to determine which length to use as
   * the width in the calculation.
   * @param {any} rotation If defined, overrides this.rotation.
   */
  getRelativeWidth(clientWidth, round, use2D, rotation) {
    if (parent) {
      const exactWidth = clientWidth * this.getWidthRatio(null, use2D, rotation);
      return round ? Math.round(exactWidth) : exactWidth;
    } else {
      return round ? Math.round(clientWidth) : clientWidth;
    }
  }

  /**
   * Returns clientHeight x getHeigthRatio(), where getHeightRatio() is the ratio of this component's
   * height relative to the parent component's height.
   * @param {any} use2D If true, then use the rotation property to determine which length to use as
   * the height in the calculation.
   * @param {any} rotation If defined, overrides this.rotation.
   */
  getRelativeHeight(clientHeight, round, use2D, rotation) {
    if (parent) {
      const exactHeight = clientHeight * this.getHeightRatio(null, use2D, rotation);
      return round ? Math.round(exactHeight) : exactHeight;
    } else {
      return round ? Math.round(clientHeight) : clientHeight;
    }
  }

  // I decided to not go with this implementation because it causes the code harder to maintain in
  // the future due to not referencing property names directly, but instead using property name strings.
  ///**
  // * This is macro helper which first checks if the property value has been modified (from the apiProduct),
  // * and if true, assign the new value to the destApiProduct object. The purpose is to build an
  // * object which only contains the modified properties. Returns true if a modification was found.
  // * @param {any} destApiProduct The API product object we are updating.
  // * @param {any} propName Used as this[propName]
  // * @param {any} apiPropName Used as this.apiProduct[apiPropName] and destApiProduct[apiPropName]
  // */
  //assignModifiedPropTo(destApiProduct, propName, apiPropName) {
  //  if (this[propName] !== this.apiProduct[apiPropName]) {
  //    destApiProduct[apiPropName] = this[propName];
  //    return true;
  //  }
  //  return false;
  //}

  /**
   * Code should assign all changes (compared to the apiProduct) to the destApiProduct. This method
   * is called from the getModifiedApiProduct() method and the returned api product might get sent
   * to the server. NOTE: if the UI changes a root property directly, this method might not be used
   * to get changes. Instead the changes are sent directly to the server. As a consequence this
   * method and the assignModificationsTo() method are not currently looking for changes to the
   * properties we know are modified directly in the UI (width, height, depth, thickness, etc.).
   * Method returns true if any changes were found.
   */
  assignModificationsTo(destApiProduct) {
    if (this.finish !== this.apiProduct.f) {
      destApiProduct.f = this.finish;
      return true;
    }
    return false;
  }

  /**
   * Is typically used for getting the changes which are sent to the server after the user has made
   * multiple changes in the UI (e.g. via a popup editor). NOTE: if the UI changes a root property
   * directly, this method might not be used to get changes. Instead the changes are sent directly
   * to the server. As a consequence this method and the assignModificationsTo() method are not
   * currently looking for changes to the properties we know are modified directly in the UI
   * (width, height, depth, thickness, etc.).
   */
  getModifiedApiProduct() {
    const apiProduct = {
      type: this.type
    };
    return this.assignModificationsTo(apiProduct) ? apiProduct : null;
  }
}

export class DoorCoreElement extends Component {
  constructor(name, drawingService, apiDoorCoreElement, config) {
    super(name, drawingService, apiDoorCoreElement, config);
  }
}

export class DoorPanel extends DoorCoreElement {
  constructor(name, drawingService, apiDoorPanel, config) {
    super(name, drawingService, apiDoorPanel, config);
  }

  preFactorySetup(config) {
    super.preFactorySetup(config);
    this.materialIdPrefix = 'DoorPanel';
  }

  getTopLeftRect() {
    const rect = super.getTopLeftRect();
    rect.relativeTop = this.parent ? rect.top / this.parent.rect.height : 0.0;
    return rect;
  }

  getTopLeftCorePanelRect() {
    let top = 0;
    let totalPanelHeight;
    const core = this.parent;
    if (core) {
      totalPanelHeight = core.getTotalPanelHeight();
      // Convert from bottom-left to top-left based position of the panel.
      top = totalPanelHeight - this.rect.bottom - this.rect.height;
    }

    //const top = this.parent ? this.parent.to2dTop(this.rect.bottom, this.rect.height) : 0;
    const rect = {
      left: this.rect.left,
      top: top,
      back: this.rect.back,
      width: this.rect.width,
      height: this.rect.height,
    };

    rect.relativeTop = totalPanelHeight ? rect.top / totalPanelHeight : 0.0;
    return rect;
  }

  createDisplayObject() {
    return this.createMaterialObject();
  }

  acceptVisitor(visitor) {
    return visitor.visitDoorPanel(this);
  }
}

export class DoorDivider extends DoorCoreElement {
  constructor(name, drawingService, apiDoorDivider, config) {
    super(name, drawingService, apiDoorDivider, config);
  }

  preFactorySetup(config) {
    super.preFactorySetup(config);
    this.dividerType = config.dividerType;
    // Api dividers don't have a finish property (always same as core's dividerF):
    this.finish = config.finish;
    this.materialIdPrefix = this.dividerType;
  }

  createDisplayObject() {
    return this.createMaterialObject();
  }

  acceptVisitor(visitor) {
    return visitor.visitDoorDivider(this);
  }
}

const DoorCoreDesign = {
  SinglePanel: 'SinglePanelDoorCore',           // Traditional single panel door style
  DualPanel: 'DualPanelDoorCore',               // Two equal size panels
  OpalDualPanel: 'OpalDualPanelDoorCore',       // Two equal size panels
  TriplePanel: 'TriplePanelDoorCore',           // Three equal size panels
  QuadruplePanel: 'QuadruplePanelDoorCore',     // Four equal size panels
  QuintuplePanel: 'QuintuplePanelDoorCore',     // Five equal size panels
  CenterPanel: 'CenterPanelDoorCore',           // One small center panel, and equally sized top and bottom panels
  FiveUnevenPanel: 'FiveUnevenPanelDoorCore',   /* Five panels, panels 1,3, and 5 are same variable height, while
                                                   panels 2 and 4 are small, 30 cm height panels */
  LargeCenterPanel: 'LargeCenterPanelDoorCore', // Same as QuintuplePanel, but with the two middle dividers removed
  LargeBottomPanel: 'LargeBottomPanelDoorCore', // Same as QuadruplePanel, but with the two bottom dividers removed
};

export class DoorCore extends Component {
  constructor(name, drawingService, apiDoorCore, config) {
    super(name, drawingService, apiDoorCore, config);
    this.design = config.design;
    this.dividerFinish = this.apiProduct.dividerF;
    this.panelLayout = config.panelLayout;
    this.panels = [];
    let i = 1;
    this.apiProduct.panels.forEach(apiPanel => {
      const component = new DoorPanel('Panel ' + i, this.drawingService, apiPanel);
      this.addComponent(component);
      this.panels.push(component);
      i++;
    });
    this.dividers = [];
    i = 1;
    this.apiProduct.dividers.forEach(apiDivider => {
      const config = { dividerType: this.apiProduct.profileType, finish: this.apiProduct.dividerF };
      const component = new DoorDivider('Divider ' + i, this.drawingService, apiDivider, config);
      this.addComponent(component);
      this.dividers.push(component);
      i++;
    });
  }

  createDisplayObject() {
    return this.createContainer();
  }

  replaceDividerFinishes(newFinish) {
    if (newFinish !== this.dividerFinish) {
      this.dividers.forEach(divider => divider.replaceFinish(newFinish));
      this.dividerFinish = newFinish;
    }
  }

  /** Called from the UI when we need to replace all the door core's panels. */
  replaceAllPanelsFinish(newFinish, onPanelChanged) {
    this.panels.forEach(panel => {
      panel.replaceFinish(newFinish, (sender, args) => {
        if (onPanelChanged) {
          args.panel = panel;
          onPanelChanged(this, args);
        }
      });
    });
  }

  /**
   * Adds the product's primary finishes to the finishes array parameter.
   */
  addToPrimaryFinishArray(finishes) {
    this.panels.forEach(panel => panel.addToPrimaryFinishArray(finishes));
  }

  assignModificationsTo(destApiProduct) {
    const arePanelsModified = Component.assignArrayModificationsTo(destApiProduct, 'panels', this.panels);
    // All dividers always have the same finish:
    //const isDividerFModified  = Component.assignArrayModificationsTo(destApiProduct, 'dividers', this.dividers);
    const isDividerFModified = this.dividerFinish !== this.apiProduct.dividerF;
    if (isDividerFModified) {
      destApiProduct.dividerF = this.dividerFinish;
    }
    return arePanelsModified || isDividerFModified;
  }

  getTopItem(items) {
    return items.length > 0 ? items[0] : null;
  }

  getBottomItem(items) {
    return items.length > 0 ? items[items.length - 1] : null;
  }

  iterate(iterator) {
    for (let i = 0, len = this.panels.length; i < len; i++) {
      iterator.iterate(this.panels[i]);
      if (i < len - 1) {
        iterator.iterate(this.dividers[i]);
      }
    }
  }

  visitChildren(visitor) {
    for (let i = 0, len = this.panels.length; i < len; i++) {
      visitor.visit(this.panels[i]);
      if (i < len - 1) {
        visitor.visit(this.dividers[i]);
      }
    }
  }

  acceptVisitor(visitor) {
    return visitor.visitDoorCore(this);
  }

  /**
   * This is a public helper method which does a simple conversion from the DoorCoreDesign enum to
   * an integer (the number of panels) given the panelLayout parameter. The getDoorCoreDesign()
   * method does the inverse.
   */
  static calculatePanelCount(panelLayout) {
    switch (panelLayout) {
      case DoorCoreDesign.SinglePanel:
        return 1;
      case DoorCoreDesign.DualPanel:
      case DoorCoreDesign.LargeBottomPanel:
        return 2;
      case DoorCoreDesign.TriplePanel:
      case DoorCoreDesign.CenterPanel:
      case DoorCoreDesign.LargeCenterPanel:
        return 3;
      case DoorCoreDesign.QuadruplePanel:
        return 4;
      case DoorCoreDesign.QuintuplePanel:
      case DoorCoreDesign.FiveUnevenPanel:
        return 5;
      default:
        return 0;
    }
  }

  /**
   * This is a public helper method which returns the DoorCoreDesign enum value corresponding to the
   * panelCount parameter. The calculatePanelCount() method does the inverse.
   */
  static getDoorCoreDesign(panelCount) {
    switch (panelCount) {
      default:
      case 1: return DoorCoreDesign.SinglePanel;
      case 2: return DoorCoreDesign.DualPanel;
      case 3: return DoorCoreDesign.TriplePanel;
      case 4: return DoorCoreDesign.QuadruplePanel;
      case 5: return DoorCoreDesign.QuintuplePanel;
    }
  }

  getTotalHeight(rects) {
    const count = rects.length;
    let height = 0;
    for (let i = 0; i < count; i++) {
      height += rects[i].height;
    }
    return height;
  }

  getTotalPanelHeight() {
    const panels = this.panels;
    const panelCount = panels.length;
    let height = 0;
    for (let i = 0; i < panelCount; i++) {
      height += panels[i].rect.height;
    }
    return height;
  }

  getVisualPanelRects() {
    const panels = this.panels;
    const panelCount = panels.length;
    const rects = Array(panelCount);
    let y = 0;
    for (let i = panelCount - 1; i >= 0; i--) {
      const source = panels[i].rect;
      rects[i] = {
        left: source.left,
        bottom: y,
        back: source.back,
        width: source.width,
        height: source.height,
      };
      y += rects[i].height;
    }
    return rects;
  }

  getTopLeftBasedVisualPanelRects() {
    const rects = this.getVisualPanelRects();
    const totalHeight = this.getTotalHeight(rects);
    for (let i = rects.length - 1; i >= 0; i--) {
      rects[i].top = totalHeight - rects[i].bottom - rects[i].height;
      delete rects[i].bottom;
    }
    return rects;
  }

  //#region Panels & Dividers

  /**
   * This is a private helper method for calculateDividerPositions().
   */
  calcVisiblePanelHeight(fixedPanelIndeces, fixedPanelVisibleHeights, defaultDivider,
    defaultDividerCount, defaultPanelCount) {
    /* Note, as of 9/9/2020 (and before) the positions and dimensions returned from the server's
     * store api are the visible ones. */
    const hasFixedPanels = fixedPanelIndeces !== null && fixedPanelIndeces.length > 0;

    const totalVisiblePanelHeight = this.rect.height - (defaultDivider.rect.height * defaultDividerCount);

    // Next find the new total height of panels with fixed height
    let totalFixedPanelVisibleHeight = 0;
    if (hasFixedPanels) {
      for (let i = 0; i < fixedPanelVisibleHeights.length; i++) {
        totalFixedPanelVisibleHeight += fixedPanelVisibleHeights[i];
      }
    }

    // The height that is available to be divided among the non-fixed panels.
    const availablePanelHeight = totalVisiblePanelHeight - totalFixedPanelVisibleHeight;
    const availablePanelCount = defaultPanelCount - (hasFixedPanels ? fixedPanelIndeces.length : 0);
    // Find the visible height per panel (out of those that don't have fixed heights)
    return availablePanelCount > 0 ? availablePanelHeight / availablePanelCount : 0;
  }

  /**
   * Calculates the positions of the default dividers in the core. These default positions are
   * then used for determining the height of the door panels after certain dividers are removed.
   * No side effects. This is a private helper method for reconfigureMultiPanelsAndDividers().
   */
  calculateDividerPositions(door, defaultPanelCount, dividerHeight, fixedPanelIndeces, fixedPanelVisibleHeights) {
    if (defaultPanelCount <= 0) {
      throw new Error('defaultPanelCount');
    }
    const defaultDividerCount = defaultPanelCount - 1;
    const dividerPositions = Array(defaultDividerCount);
    if (defaultPanelCount == 1) {
      return dividerPositions;
    }
    const hasFixedPanels = fixedPanelIndeces !== null && fixedPanelIndeces.length > 0;
    const visiblePanelHeight = this.calcVisiblePanelHeight(fixedPanelIndeces, fixedPanelVisibleHeights,
      this.getTopItem(this.dividers), defaultDividerCount, defaultPanelCount);

    let fixedPanelIndex = hasFixedPanels ? fixedPanelIndeces.length - 1 : -1;

    //const finish = this.getBottomItem(this.panels).finish;
    const tongueLength = 0; // door !== null ? door.frame.bottomRail.panelTongueLength(finish) : 0.0; // TODO!!!!
    let currentPanelVisibleOffset = tongueLength;
    for (let i = defaultDividerCount - 1; i > -1; i--) {
      let panelHeight;
      if (hasFixedPanels && fixedPanelIndex > -1 && i + 1 === fixedPanelIndeces[fixedPanelIndex]) {
        panelHeight = fixedPanelVisibleHeights[fixedPanelIndex];
        fixedPanelIndex--;
      } else {
        panelHeight = visiblePanelHeight;
      }
      dividerPositions[i] = currentPanelVisibleOffset + panelHeight;
      currentPanelVisibleOffset = dividerPositions[i] + dividerHeight;
    }
    return dividerPositions;
  }

  /**
   * Updates panel and divider positions and panel heights. Does not update any display objects.
   * This is a private helper method for the reconfigurePanelsAndDividers() method.
   */
  reconfigureMultiPanelsAndDividers(door, fixedPanelIndeces, fixedPanelVisibleHeights) {
    /* Note, as of 9/9/2020 (and before) the positions and dimensions returned from the server's
     * store api are the visible ones. We don't have information about the size of panel indents
     * into frames and dividers (tongueAbove/-Below are 0 in this method). */
    const panels = this.panels;
    const panelCount = panels.length;
    switch (panelCount) {
      case 0:
        break;
      case 1:
        panels[0].rect.bottom = 0;
        break;
      default: {
        let defaultPanelCount = DoorCore.calculatePanelCount(this.panelLayout);
        const dividerHeight = this.getTopItem(this.dividers).rect.height;
        const isLargeCenterPanel = this.panelLayout === DoorCoreDesign.LargeCenterPanel;
        const isLargeBottomPanel = this.panelLayout === DoorCoreDesign.LargeBottomPanel;
        if (isLargeCenterPanel) {
          // Use the layout of a 5-panel core as the starting point
          defaultPanelCount += 2;
        } else if (isLargeBottomPanel) {
          // Use the layout of a 4-panel core as the starting point
          defaultPanelCount += 2;
        }
        const dividerPositions = this.calculateDividerPositions(door, defaultPanelCount, dividerHeight,
          fixedPanelIndeces, fixedPanelVisibleHeights);
        if (isLargeCenterPanel) {
          // Move the value of the last position into the array's second slot:
          dividerPositions[1] = dividerPositions[3];
          dividerPositions.length = 2;
        } else if (isLargeBottomPanel) {
          dividerPositions.length = 1;
        }
        const lastPanelIndex = panelCount - 1;
        //const frame = door != null ? door.frame : null;
        //let frameBelow = frame != null ? frame.topRail : null;
        let dividerIndex = -1;
        let priorDividerPosition = 0;
        for (let i = 0; i < panelCount; i++) {
          //const frameAbove = frameBelow;
          //frameBelow = i === lastPanelIndex
          //  ? frame != null ? frame.bottomRail : null
          //  : this.dividers[i];

          const panel = this.panels[i];
          //const finish = panel.finish;
          const tongueAbove = 0; //frameAbove != null ? frameAbove.getPanelTongueLength(finish) : 0.0; // TODO
          const tongueBelow = 0; //frameBelow != null ? frameBelow.getPanelTongueLength(finish) : 0.0; // TODO

          if (i === 0) {
            priorDividerPosition = this.rect.height - tongueAbove;
            if (priorDividerPosition < 0) {
              priorDividerPosition = 0;
            }
          }

          let visibleHeight;
          dividerIndex += panel.MergedPanels || 1; // TODO: 1 = no merged panels, 2 = 2 panels were merged to create this panel
          if (dividerIndex < dividerPositions.length) {
            visibleHeight = priorDividerPosition - dividerPositions[dividerIndex] - dividerHeight;
            priorDividerPosition = dividerPositions[dividerIndex];
          } else {
            visibleHeight = priorDividerPosition - tongueBelow;
          }
          if (visibleHeight < 0)
            visibleHeight = 0;

          panel.rect.height = visibleHeight;
          if (i == lastPanelIndex) {
            this.panels[i].rect.bottom = tongueBelow;
          } else if (dividerIndex < dividerPositions.length) {
            this.dividers[i].rect.bottom = dividerPositions[dividerIndex];
            this.panels[i].rect.bottom = dividerPositions[dividerIndex] + dividerHeight;
          }
        }

        break;
      }
    }
  }

  /**
   * Return a Divider object which can be used as the template for new dividers added to
   * the door core. If the core doesn't have any dividers, the onFindTemplateDivider callback
   * function is called. This is a private helper method for updatePanelAndDividerComponentLists and
   * shouldn't be used for other purposes.
   */
  getTemplateDivider() {
    let divider = this.getBottomItem(this.dividers);
    if (!divider && this.onFindTemplateDivider) {
      divider = this.onFindTemplateDivider(this);
    }
    return divider;
  }

  /**
   * Calculates the current number of panels and dividers and updates the component lists to
   * contain the correct number of components. Display objects will be removed/added as needed
   * as well. Component positions and sizes are not updated in this method. This is a private helper
   * method for reconfigurePanelsAndDividers and shouldn't be used for other purposes.
   */
  updatePanelAndDividerComponentLists() {
    const panelCount = DoorCore.calculatePanelCount(this.panelLayout);
    let template = this.getBottomItem(this.panels);
    let i = panelCount;
    while (this.panels.length < panelCount) {
      const apiPanel = {
        /* type only needs to be assigned if/when type can be modified or different among
         * the children (e.g. section contents). */
        //type: 'Panel', // TODO
        f: template.finish,
        l: template.rect.left,
        b: template.rect.bottom,
        z: template.rect.back,
        w: template.rect.width,
        h: template.rect.height,
      };
      const panel = new DoorPanel('Panel ' + i, this.drawingService, apiPanel);
      this.addComponent(panel);
      this.panels.push(panel);
      panel.addDisplayObject();
      i++;
    }

    while (this.panels.length > panelCount) {
      const panel = this.panels.pop();
      panel.removeDisplayObject();
      this.removeComponent(panel);
    }

    const dividerCount = panelCount - 1;
    template = this.getTemplateDivider();
    i = dividerCount;
    while (this.dividers.length < dividerCount) {
      const apiDivider = {
        //type only needs to be assigned if/when type can be modified or different among the children (e.g. section contents)
        //type: 'Divider', // TODO
        f: this.dividerFinish,
        l: template.rect.left,
        b: template.rect.bottom,
        z: template.rect.back,
        w: template.rect.width,
        h: template.rect.height,
      };
      const config = { dividerType: this.apiProduct.profileType, finish: this.apiProduct.dividerF };
      const divider = new DoorDivider('Divider ' + i, this.drawingService, apiDivider, config);
      this.addComponent(divider);
      this.dividers.push(divider);
      divider.addDisplayObject();
      i++;
    }
    while (this.dividers.length > dividerCount) {
      const divider = this.dividers.pop();
      divider.removeDisplayObject();
      this.removeComponent(divider);
    }
  }

  /**
   * Reconfigures the panels and dividers in the door core based on the current value of the
   * panelLayout property. This method is called from the Door.replaceLayout() method.
   */
  reconfigurePanelsAndDividers(door) {
    /* Step 1 of 3: add/remove panels and dividers as needed. This method will remove obsolete
     * display objects, add new ones, but does not calculate positions and sizes. */

    this.updatePanelAndDividerComponentLists();

    /* Step 2 of 3: update panel and divider positions, and panel sizes (doesn't affect display objects): */

    const panels = this.panels;
    let indeces;
    let heights;
    let height;
    switch (this.panelLayout) {
      case DoorCoreDesign.SinglePanel:
        if (panels.length === 1) {
          // TODO: This doesn't account for the top/bottom rail heights:
          const panel = this.getTopItem(panels);
          panel.rect.height = this.rect.height;
          panel.rect.bottom = door.frame.bottomRail.rect.bottom; // TODO: Shouldn't this be rect.top?
        }
        break;
      case DoorCoreDesign.DualPanel:
      case DoorCoreDesign.TriplePanel:
      case DoorCoreDesign.QuadruplePanel:
      case DoorCoreDesign.QuintuplePanel:
      case DoorCoreDesign.LargeCenterPanel:
      case DoorCoreDesign.LargeBottomPanel:
        this.reconfigureMultiPanelsAndDividers(door, null, null);
        break;
      case DoorCoreDesign.CenterPanel:
        indeces = Array(1);
        height = panels.length > 1 ? panels[1].height : 0;
        heights = [height];
        this.reconfigureMultiPanelsAndDividers(door, indeces, heights);
        break;
      case DoorCoreDesign.FiveUnevenPanel:
        indeces = [1, 3];
        height = panels.length > 1 ? panels[1].height : 0;
        heights = [height, height];
        this.reconfigureMultiPanelsAndDividers(door, indeces, heights);
        break;
      default:
        break;
    }

    /* Step 3 of 3: update display objects with position, size, and finish of each panel and divider. */

    const updateElement = element => {
      this.drawingService.setDisplayObjectPosition(element);
      // Don't know why the line below was here, it'll never do anything since it's replacing the finish with its own finish
      //this.replaceChildFinish(element, element.finish);
      //element.replaceFinish(element.finish); // ...also this is the same thing, but easier (still doesn't do anything, though)
    };
    this.panels.forEach(panel => updateElement(panel));
    this.dividers.forEach(divider => updateElement(divider));
  }

  //#endregion (Panels & Dividers)
}

export class DoorFrameElement extends Component {
  constructor(name, drawingService, apiDoorFrameElement, config) {
    super(name, drawingService, apiDoorFrameElement, config);
  }

  preFactorySetup(config) {
    super.preFactorySetup(config);
    this.design = config.design;
    // apiDoorFrameElement doesn't have a finish (always the same as the parent frame)
    this.finish = config.finish;
  }

  acceptVisitor(visitor) {
    return visitor.visitFrameElement(this);
  }
}

export class DoorRail extends DoorFrameElement {
  constructor(name, drawingService, apiDoorRail, config) {
    super(name, drawingService, apiDoorRail, config);
  }

  preFactorySetup(config) {
    super.preFactorySetup(config);
    this.isTop = config.isTop;
    if (this.design === 'Safir') {
      const tb = this.isTop ? 'Top' : 'Bottom';
      this.materialIdPrefix = this.design + tb + 'DoorRail';
    } else {
      this.materialIdPrefix = this.design + 'DoorRail';
    }
  }

  createDisplayObject() {
    return this.createMaterialObject();
  }

  acceptVisitor(visitor) {
    return visitor.visitDoorRail(this);
  }
}

export class DoorStile extends DoorFrameElement {
  constructor(name, drawingService, apiDoorStile, config) {
    super(name, drawingService, apiDoorStile, config);
    this.isLeft = config.isLeft;
    if (this.isLeft) {
      /* The stile images are of the right stile. To display on the left side,
       * flip the image or rotate 180 degrees: */
      this.rotation.z = 180;
    }
  }

  preFactorySetup(config) {
    super.preFactorySetup(config);
    this.materialIdPrefix = this.design + 'DoorStile';
  }

  createDisplayObject() {
    return this.createMaterialObject();
  }

  acceptVisitor(visitor) {
    return visitor.visitDoorStile(this);
  }
}

export class DoorFrame extends Component {
  constructor(name, drawingService, apiDoorFrame, config) {
    super(name, drawingService, apiDoorFrame, config);
    this.design = config.design;

    const topConfig = { design: this.design, finish: apiDoorFrame.f, isTop: true };
    this.topRail = new DoorRail('Top Rail', this.drawingService, apiDoorFrame.topRail, topConfig);
    this.addComponent(this.topRail);
    const botConfig = { design: this.design, finish: apiDoorFrame.f, isTop: false };
    this.bottomRail = new DoorRail('Bottom Rail', this.drawingService, apiDoorFrame.bottomRail, botConfig);
    this.addComponent(this.bottomRail);

    const leftConfig = { design: this.design, finish: apiDoorFrame.f, isLeft: true };
    this.leftStile = new DoorStile('Left Stile', this.drawingService, apiDoorFrame.leftStile, leftConfig);
    this.addComponent(this.leftStile);
    const rightConfig = { design: this.design, finish: apiDoorFrame.f, isLeft: false };
    this.rightStile = new DoorStile('Right Stile', this.drawingService, apiDoorFrame.rightStile, rightConfig);
    this.addComponent(this.rightStile);
  }

  createDisplayObject() {
    return this.createContainer();
  }

  replaceChildFinishes(newFinish) {
    if (newFinish !== this.finish) {
      this.components.forEach(component => component.replaceFinish(newFinish));
      this.setFinish(newFinish);
    }
  }

  iterate(iterator) {
    iterator.iterate(this.topRail);
    iterator.iterate(this.bottomRail);
    iterator.iterate(this.leftStile);
    iterator.iterate(this.rightStile);
  }

  visitChildren(visitor) {
    visitor.visit(this.topRail);
    visitor.visit(this.bottomRail);
    visitor.visit(this.leftStile);
    visitor.visit(this.rightStile);
  }

  acceptVisitor(visitor) {
    return visitor.visitDoorFrame(this);
  }
}

export class DoorBottomTrack extends Component {
  constructor(name, drawingService, apiBottomTrack, config) {
    super(name, drawingService, apiBottomTrack, config);
  }

  preFactorySetup(config) {
    super.preFactorySetup(config);
    this.materialIdPrefix = 'SlidingDoorBottomTrack-' + this.apiProduct.trackType;
  }

  createDisplayObject() {
    return this.createMaterialObject();
  }

  acceptVisitor(visitor) {
    return visitor.visitDoorBottomTrack(this);
  }
}

export class DoorTopTrack extends Component {
  constructor(name, drawingService, apiTopTrack, config) {
    super(name, drawingService, apiTopTrack, config);
  }

  preFactorySetup(config) {
    super.preFactorySetup(config);
    this.materialIdPrefix = 'SlidingDoorTopTrack-' + this.apiProduct.trackType;
  }

  createDisplayObject() {
    return this.createMaterialObject();
  }

  acceptVisitor(visitor) {
    return visitor.visitDoorTopTrack(this);
  }
}

export class Door extends Component {
  constructor(name, drawingService, apiDoor, config) {
    super(name, drawingService, apiDoor, config);
    this.doorIndex = config ? config.doorIndex : 0; // The index of this door in the owner door front's array of doors.
    this.doorIndex = this.doorIndex || 0;
    this.panelLayout = apiDoor.panelLayout;
    this.railNo = apiDoor.railNo;
    const coreConfig = { panelLayout: this.panelLayout, design: apiDoor.design };
    this.core = new DoorCore('Core', this.drawingService, apiDoor.core, coreConfig);
    this.core.onFindTemplateDivider = core => this.handleOnFindTemplateDivider(core);
    this.addComponent(this.core);
    const frameConfig = { design: apiDoor.design };
    this.frame = new DoorFrame('Frame', this.drawingService, apiDoor.frame, frameConfig);
    this.addComponent(this.frame);
  }

  handleOnFindTemplateDivider(core) {
    let divider = this.onFindTemplateDivider ? this.onFindTemplateDivider(this) : null;
    if (divider === null) {
      /* No divider to be used as template was found. Let's construct a template using the
       * information we have. */
      // TODO: We need to get this info from the server
      let height = this.frame.topRail.rect.height;
      switch (this.frame.design) {
        case 'Safir':
          height = 34;
          break;
        case 'Topaz':
          height = 100;
          break;
      }
      divider = {
        finish: this.core.dividerFinish,
        rect: {
          left: 0,
          bottom: 0,
          back: 0,
          width: this.core.rect.width,
          height: height,
          depth: this.core.rect.depth,
        }
      };
    }
    return divider;
  }

  findTemplateDivider(targetDoor) {
    if (targetDoor !== this) {
      return this.core.getBottomItem(this.core.dividers);
    }
    return null;
  }

  createDisplayObject() {
    return this.createContainer();
  }

  replaceFrameFinish(newFinish, onChanged) {
    if (newFinish !== this.frame.finish) {
      const oldFinish = this.frame.finish;
      this.frame.replaceChildFinishes(newFinish);
      this.core.replaceDividerFinishes(newFinish);
      if (onChanged) {
        onChanged(this, { oldFinish });
      }
      return true;
    }
    return false;
  }

  /** Called from the UI when we need to replace all the door's panels. */
  replaceAllPanelsFinish(newFinish, onPanelChanged) {
    this.core.replaceAllPanelsFinish(newFinish, (cor, args) => onPanelChanged(this, args));
  }

  /**
   * This method is called from the UI after the user has changed the door's panel layout.
   */
  replaceLayout(newLayout, onChanged) {
    if (newLayout !== this.panelLayout) {
      const oldLayout = this.panelLayout;
      this.drawingService.disableDrawingUpdates();
      try {
        this.panelLayout = newLayout;
        this.core.panelLayout = newLayout;
        this.core.reconfigurePanelsAndDividers(this);
      } finally {
        if (this.drawingService.enableDrawingUpdates()) {
          this.drawingService.doorLayoutWasReplaced(this);
        }
      }
      if (onChanged) {
        onChanged(this, { oldLayout });
      }
    }
  }

  assignModificationsTo(destApiProduct) {
    let isModified = false;
    if (this.panelLayout !== this.apiProduct.panelLayout) {
      destApiProduct.panelLayout = this.panelLayout;
      isModified = true;
    }
    const destCore = {
      //type only needs to be assigned if/when type can be modified or different among the children (e.g. section contents)
      //type: this.core.type
    };
    const isCoreModified = this.core.assignModificationsTo(destCore);
    if (isCoreModified) {
      destApiProduct.core = destCore;
    }
    const destFrame = {
      //type only needs to be assigned if/when type can be modified or different among the children (e.g. section contents)
      //type: this.frame.type
    };
    const isFrameModified = this.frame.assignModificationsTo(destFrame);
    if (isFrameModified) {
      destApiProduct.frame = destFrame;
    }
    return isModified || isCoreModified || isFrameModified;
  }

  iterate(iterator) {
    iterator.iterate(this.frame);
    iterator.iterate(this.core);
  }

  visitChildren(visitor) {
    visitor.visit(this.frame);
    visitor.visit(this.core);
  }

  acceptVisitor(visitor) {
    return visitor.visitDoor(this);
  }
}

export class DoorFront extends Component {
  constructor(name, drawingService, apiDoorFront, config) {
    super(name, drawingService, apiDoorFront, config);

    const addDoor = (doors, i, doorConfig) => {
      doorConfig.doorIndex = i;
      const doorComp = new Door(`Door ${i + 1}`, this.drawingService, doors[i], doorConfig);
      doorComp.onFindTemplateDivider = door => this.handleOnFindTemplateDivider(door);
      this.addComponent(doorComp);
      this.doors.push(doorComp);
    }

    const doors = this.apiProduct.doors;
    this.doors = [];
    const doorConfig = { doorIndex: 0 };

    if (drawingService.addOddDoorsFirst()) {
      /* We want to add the odd indexed doors first so that every second door (even indexed)
       * is displayed after (on top) of the odd doors. */
      let i;
      for (i = 0; i < doors.length; i++) {
        if (i % 2 === 0) {
          //doorConfig.doorIndex = i;
          //const doorComp = new Door(`Door ${i + 1}`, this.drawingService, doors[i], doorConfig);
          //doorComp.onFindTemplateDivider = door => this.handleOnFindTemplateDivider(door);
          //this.addComponent(doorComp);
          //this.doors.push(doorComp);
          addDoor(doors, i, doorConfig);
        }
      }
      for (i = 0; i < doors.length; i++) {
        if (i % 2 === 1) {
          //doorConfig.doorIndex = i;
          //const doorComp = new Door(`Door ${i + 1}`, this.drawingService, doors[i], doorConfig);
          //doorComp.onFindTemplateDivider = door => this.handleOnFindTemplateDivider(door);
          //this.addComponent(doorComp);
          //this.doors.push(doorComp);
          addDoor(doors, i, doorConfig);
        }
      }
    } else {
      for (let i = 0; i < doors.length; i++) {
        addDoor(doors, i, doorConfig);
      }
    }

    this.railCount = apiDoorFront.railCount;
    this.topTrack = new DoorTopTrack('Top Track', this.drawingService, apiDoorFront.topTrack);
    this.addComponent(this.topTrack);
    this.bottomTrack = new DoorBottomTrack('Bottom Track', this.drawingService, apiDoorFront.bottomTrack);
    this.addComponent(this.bottomTrack);
  }

  handleOnFindTemplateDivider(targetDoor) {
    let foundDivider = null;
    this.doors.forEach(door => {
      foundDivider = foundDivider || door.findTemplateDivider(targetDoor);
    });
    return foundDivider;
  }

  createDisplayObject() {
    return this.createContainer();
  }

  /** Replaces the frame finish on all doors. */
  replaceFrameFinish(newFinish, onDoorFrameChanged) {
    this.drawingService.disableDrawingUpdates();
    try {
      this.doors.forEach(door => door.replaceFrameFinish(newFinish, (dor, data) => {
        if (onDoorFrameChanged) {
          onDoorFrameChanged(dor, data);
        }
      }));
    } finally {
      if (this.drawingService.enableDrawingUpdates()) {
        this.drawingService.performInlineUpdate(this);
      }
    }
  }

  /** Called from the UI when we need to replace all panels on all doors. */
  replaceAllPanelsFinish(newFinish, onPanelChanged) {
    this.doors.forEach(door => {
      door.replaceAllPanelsFinish(newFinish, (dor, args) => {
        if (onPanelChanged) {
          args.door = dor;
          onPanelChanged(this, args);
        }
      });
    });
  }

  /**
   * Adds the product's primary finishes to the finishes array parameter.
   */
  addToPrimaryFinishArray(finishes) {
    this.doors.forEach(door => door.core.addToPrimaryFinishArray(finishes));
  }

  /**
   * Adds the doors' frame finishes to the secondary finishes array parameter.
   */
  addToSecondaryFinishArray(finishes) {
    this.doors.forEach(door => door.frame.addToPrimaryFinishArray(finishes));
  }

  getAllFrameFinishes() {
    return this.getAllSecondaryFinishes();
  }

  getAllTrackFinishes() {
    return [this.topTrack.finish];
  }

  getAllPanelLayouts() {
    let layouts = [];
    this.doors.forEach(door => {
      if (!layouts.includes(door.panelLayout)) {
        layouts.push(door.panelLayout);
      }
    });
    return layouts;
  }

  allDoorsHaveSinglePanel() {
    const found = !this.doors.find(door => door.panelLayout !== 'SinglePanelDoorCore');
    return found;
  }

  /**
   * Returns the overlap beetween the door and the door to the left.
   */
  getLeftOverlap(door) {
    const index = door.doorIndex || this.doors.indexOf(door);
    if (index < 1) {
      return 0;
    }
    //let totWidth = 0;
    //for (var i = 0; i < index; i++) {
    //  totWidth += this.doors[i].rect.width;
    //}
    const priorDoor = this.doors[index - 1];
    const left = priorDoor.rect.left + priorDoor.rect.width;
    return left - door.rect.left;
  }

  assignModificationsTo(destApiProduct) {
    // Create a copy of the doors array, and sort the doors by original index
    const doors = this.doors.slice();
    doors.sort((a, b) => a.doorIndex - b.doorIndex);
    const areDoorsModified = Component.assignArrayModificationsTo(destApiProduct, 'doors', doors);

    const destTopTrack = {
      //type only needs to be assigned if/when type can be modified or different among the children (e.g. section contents)
      //type: this.topTrack.type
    };
    const isTopTrackModified = this.topTrack.assignModificationsTo(destTopTrack);
    if (isTopTrackModified) {
      destApiProduct.topTrack = destTopTrack;
    }
    const destBottomTrack = {
      //type only needs to be assigned if/when type can be modified or different among the children (e.g. section contents)
      //type: this.bottomTrack.type
    };
    const isBottomTrackModified = this.bottomTrack.assignModificationsTo(destBottomTrack);
    if (isBottomTrackModified) {
      destApiProduct.bottomTrack = destBottomTrack;
    }
    return areDoorsModified || isTopTrackModified || isBottomTrackModified;
  }

  iterate(iterator) {
    this.doors.forEach(door => iterator.iterate(door));
    iterator.iterate(this.bottomTrack);
    iterator.iterate(this.topTrack);
  }

  visitChildren(visitor) {
    this.doors.forEach(door => visitor.visit(door));
    visitor.visit(this.bottomTrack);
    visitor.visit(this.topTrack);
  }

  acceptVisitor(visitor) {
    return visitor.visitDoorFront(this);
  }
}

export class SideWall extends Component {
  constructor(name, drawingService, apiSideWall) {
    super(name, drawingService, apiSideWall);
  }
  preFactorySetup(config) {
    super.preFactorySetup(config);
    this.materialIdPrefix = 'SideWall';
  }

  createDisplayObject() {
    return this.createMaterialObject();
  }

  acceptVisitor(visitor) {
    return visitor.visitSideWall(this);
  }
}

export class FixedProduct extends Component {
}
export class OneDimVariantProduct extends Component {
}
export class MultiDimVariantProduct extends Component {
}